import React, { useCallback, useEffect, useState } from "react";
import { Users, ClipboardCheck, ShieldCheck, AlertTriangle, FileSearch, ServerCog } from "lucide-react";
import { Link } from "react-router-dom";
import { employeeApi } from "../../services/employeeApi";
import { auditApi } from "../../services/auditApi";
import { documentApi } from "../../services/documentApi";
import { AuditTimeline } from "../../components/AuditTimeline";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useRouteRefresh } from "../../hooks/useRouteRefresh";
import { useLocalization } from "../../hooks/useLocalization";
import { usePermissions } from "../../hooks/usePermissions";
import { useSystemJobsFailureCount } from "../../hooks/useSystemJobsFailureCount";

const StatCard = ({ icon: Icon, label, value, testId, accent, to }) => {
  const content = (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow" data-testid={testId}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${accent}`}>
        <Icon size={20} className="text-white" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
};

export default function DashboardPage() {
  const { t } = useLocalization();
  const perm = usePermissions();
  const [stats, setStats] = useState({ total: 0, cleared: 0, blocked: 0, active_onboarding: 0 });
  const [events, setEvents] = useState([]);
  const [docQueue, setDocQueue] = useState([]);
  const jobFailureCount = useSystemJobsFailureCount(perm.canViewSystemJobs);

  const load = useCallback(() => {
    employeeApi.stats().then((r) => setStats(r.data)).catch(() => {});
    auditApi.list({ page: 1, page_size: 10 }).then((r) => setEvents(r.data.items)).catch(() => {});
    documentApi.queue({}).then((r) => setDocQueue(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useRouteRefresh(load);

  const overdueDocs = docQueue.filter((d) => (d.days_waiting || 0) >= 3).length;
  const needingCorrection = docQueue.filter((d) => d.status === "NEEDS_CORRECTION").length;

  return (
    <div data-testid="dashboard-page">
      <PageHelpBanner pageKey="dashboard" title={t("pageHelp.defaultTitle")}>
        <p>{t("dashboard.help")}</p>
      </PageHelpBanner>

      {jobFailureCount > 0 && (
        <Link
          to="/management/system/jobs?status=FAILED"
          data-testid="dashboard-job-failure-banner"
          className="flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 mb-6 hover:bg-amber-100 transition-colors"
        >
          <ServerCog size={18} className="text-amber-700 shrink-0" />
          <p className="text-sm font-medium text-amber-800">
            {t("systemJobs.dashboardBanner", { count: jobFailureCount })}
          </p>
        </Link>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label={t("dashboard.totalEmployees")} value={stats.total} testId="stat-total-employees" accent="bg-blue-600" />
        <StatCard icon={ClipboardCheck} label={t("dashboard.activeOnboarding")} value={stats.active_onboarding} testId="stat-active-onboarding" accent="bg-amber-500" />
        <StatCard icon={ShieldCheck} label={t("dashboard.clearedToWork")} value={stats.cleared} testId="stat-cleared" accent="bg-emerald-500" />
        <StatCard icon={AlertTriangle} label={t("dashboard.blockedCases")} value={stats.blocked} testId="stat-blocked" accent="bg-red-500" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard icon={FileSearch} label={t("documentReview.pendingReviews")} value={docQueue.length} testId="stat-pending-reviews" accent="bg-blue-500" to="/management/document-review" />
        <StatCard icon={AlertTriangle} label={t("documentReview.overdueReviews")} value={overdueDocs} testId="stat-overdue-reviews" accent="bg-red-500" to="/management/document-review" />
        <StatCard icon={ClipboardCheck} label={t("documentReview.needingCorrection")} value={needingCorrection} testId="stat-needing-correction" accent="bg-amber-500" to="/management/document-review" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t("dashboard.recentActivity")}</h3>
        <AuditTimeline events={events} />
      </div>
    </div>
  );
}
